/**
 * useFunnelAddToCart Hook
 *
 * Reusable hook that provides a funnel-aware add-to-cart callback.
 * Handles SKU/variant resolution for funnel offers and integrates
 * with the storefront cart system.
 *
 * Used by FunnelExperience on all page types (PDP, homepage, collection).
 */

'use client';

import { useCallback } from 'react';
import { useAddToCart } from './use-product';
import { useCartContext } from '@/lib/context/cart-context';
import { useIntl } from '@/lib/intl';
import { productService } from '@/infrastructure/config/services.config';
import { storefrontLayoutLogger } from '@/lib/logger';
import { useFunnelContext } from '@template-project/storefront-funnel';
import { currencyRatioFromAmounts } from '@/domain/value-objects/currency-amount.vo';

interface UseFunnelAddToCartOptions {
  /** Override country code (e.g. from PDP props). Falls back to intl config. */
  countryCode?: string;
  /** Override currency (e.g. from PDP props). Falls back to intl config. */
  currency?: string;
  vendor?: string;
  shop?: string;
}

/**
 * Returns a callback compatible with FunnelExperience's onAddToCart prop.
 *
 * The callback resolves product/variant IDs (which may be SKUs from the funnel offer)
 * into real UUIDs, then adds the item to the cart.
 */
export function useFunnelAddToCart(options: UseFunnelAddToCartOptions = {}) {
  const addToCartMutation = useAddToCart();
  const { cartToken, setCartToken, showCart } = useCartContext();
  const { countryConfig } = useIntl();
  const { assignedFunnelId, activeExperience } = useFunnelContext();

  const countryCode = options.countryCode || countryConfig.countryCode;
  const currency = options.currency || countryConfig.currency.code;
  const vendor = options.vendor || 'shapermint';
  const shop = options.shop || 'shapermint';

  const persistedFunnelId = assignedFunnelId ?? activeExperience?.funnelId ?? null;

  const handleFunnelAddToCart = useCallback(
    async (productId: string, variantId: string, quantity: number) => {
      try {
        let resolvedProductId = productId;
        let resolvedVariantId = variantId;

        const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const productIsUuid = UUID_RE.test(productId);
        const variantIsUuid = UUID_RE.test(variantId);

        // If both IDs are valid UUIDs, use them directly.
        // Otherwise resolve via product catalog (SKU/slug → UUID).
        // When variantId is missing (common for funnel-level offers that inherit
        // from funnel_offer_id), fetch the product to get its first variant.
        const pricingOpts = { currency, country: countryCode };
        let offerProduct = null;

        if (!variantIsUuid || !productIsUuid) {
          offerProduct = productIsUuid
            ? await productService.getProductById(productId, pricingOpts).catch(() => null)
            : await productService.getProductBySlug(productId, pricingOpts).catch(() => null);
          if (offerProduct) {
            resolvedProductId = offerProduct.uuid ?? offerProduct.id ?? resolvedProductId;
            resolvedVariantId =
              offerProduct.variants.find((v) => v.enabled)?.id ||
              offerProduct.variants[0]?.id ||
              resolvedVariantId;
          }
        }

        const resolvedVariant = offerProduct?.variants.find((v) => v.id === resolvedVariantId);
        const unitPrice = resolvedVariant?.price ?? offerProduct?.price;
        const currencyRatio =
          unitPrice && unitPrice.display > 0
            ? currencyRatioFromAmounts(unitPrice.base, unitPrice.display)
            : 1;

        if (!resolvedVariantId || !UUID_RE.test(resolvedVariantId)) {
          storefrontLayoutLogger.error('[useFunnelAddToCart] Could not resolve valid UUID variant for funnel offer', undefined, {
            productId,
            variantId,
            resolvedProductId,
            resolvedVariantId: resolvedVariantId || null,
          });
          return;
        }

        const result = await addToCartMutation.mutateAsync({
          cartToken: cartToken || undefined,
          productId: resolvedProductId,
          variantId: resolvedVariantId,
          quantity,
          options: {
            countryCode,
            currency,
            currencyRatio,
            vendor,
            shop,
            ...(persistedFunnelId ? { upsellFunnelId: persistedFunnelId } : {}),
          },
        });

        if (result.cartToken) {
          setCartToken(result.cartToken);
        }

        showCart();
      } catch (error) {
        storefrontLayoutLogger.error(
          '[useFunnelAddToCart] Failed to add funnel offer to cart',
          error instanceof Error ? error : undefined,
          { productId, variantId, quantity },
        );
        throw error;
      }
    },
    [addToCartMutation, persistedFunnelId, cartToken, setCartToken, showCart, countryCode, currency, vendor, shop],
  );

  return handleFunnelAddToCart;
}
